import { resetUserSession } from '@/services/resetUserSession';

/** Why the session ended, carried across the redirect for the sign-in page to show. */
export type TSessionEndReason = 'expired' | 'wrong-platform';

export const SESSION_END_REASON_KEY = 'authEndReason';

/**
 * The in-flight teardown, if there is one.
 *
 * De-duplicating with a memoised promise rather than a boolean matters: an early
 * `if (ending) return` would tell a concurrent caller that a teardown exists
 * without letting it await one, so that caller would carry straight on to its own
 * next step while state was still being torn down.
 */
let teardown: Promise<void> | null = null;

export const isSessionEnding = (): boolean => teardown !== null;

/** Optional side effects must never be able to prevent the redirect. */
const bestEffort = (label: string, fn: () => void) => {
  try {
    fn();
  } catch (error) {
    console.warn(`endSession: ${label} failed`, error);
  }
};

/**
 * End the session exactly once: clear local state, then leave for /sign-in.
 *
 * Never rejects — sign-out is documented as unable to throw and is built on this.
 * Optional work is individually guarded so it can never prevent the navigation: sessionStorage.setItem throws outright in Safari
 * private mode and on a full quota, and that, before an unguarded redirect, would
 * strand the user on a dead authenticated page with no error. The reason code is
 * a convenience; leaving is not.
 *
 * No telemetry reset here — this app has no telemetry provider.
 */
/**
 * Clear local state. Runs *after* the navigation, so the authenticated tree has
 * already unmounted and cannot re-render against an emptied store.
 */
const clearLocalState = () => {
  resetUserSession();
};

export const endSession = (opts?: { reason?: TSessionEndReason }): Promise<void> =>
  (teardown ??= (async () => {
    // Stash the reason before navigating — /sign-in reads it on mount.
    bestEffort('reason', () => {
      if (opts?.reason) sessionStorage.setItem(SESSION_END_REASON_KEY, opts.reason);
    });

    try {
      // Lazy import, because a static one would be a cycle: services/api imports
      // this module, and the router imports the route tree, which imports
      // services/api. At module-init time `router` could be undefined.
      const { router } = await import('@/router');

      await router.navigate({ to: '/sign-in', replace: true });
    } catch {
      // SPA navigation failed. Tear down and leave by force rather than strand the
      // user inside the authenticated shell with a dead session.
      try {
        clearLocalState();
      } finally {
        window.location.replace('/sign-in');
      }

      return;
    }

    clearLocalState();
  })()
    .catch(() => {})
    .finally(() => {
      teardown = null;
    }));
